// Derived summaries computed from normalized dayRows (buildDayRows) for the
// Summary and Reports views. Related concerns in one module:
//   buildEnergyExpenditureStats / suggestedIntakeFor / weightSlopePerDay — expenditure estimation
//   buildSummaryStats          — metric card aggregates
//   buildChartSeries           — hero/reports intake-vs-bodyweight chart data points
//   buildMacroBreakdownSeries  — reports macro-breakdown chart data points
//   buildMacroAdherenceTable   — reports macro-breakdown Target/Consumed/Adherence table
//   buildWeekdayAdherenceSeries — reports weekday-adherence chart data points
// All are pure functions with no React dependency.

import moment from "moment";

import {
  avgOf,
  dayAdherence,
  dayDeviation,
  macroCol,
  weekdayGroups,
  weekGroups,
} from "./adherence";
import { buildBodyweightTrend } from "./bodyweightTrend";
import { MACROS } from "./dayData";

/* ---- energy expenditure ----------------------------------------------- */

const EXPENDITURE_NEEDS = { minDays: 14, minLogged: 10, minWeighIns: 6 };

// If the regression slope implies a weekly weight change above this, it likely
// includes water/glycogen swings rather than fat — the TDEE back-calc is unreliable.
const DECOUPLING_THRESHOLD_KG_PER_WEEK = 1;

// A window can clear the minLogged/minWeighIns gate entirely from data near
// its start (e.g. a client who logged actively months ago, then stopped) —
// the resulting estExpenditure would be "confident" but describe an old
// stretch, not the client's current behavior. Flag it (staleData) rather
// than blocking it, same treatment as `decoupled` below.
const RECENT_WINDOW_DAYS = 30;

// The 3 override fields, normalized once from the raw nutrition-tdee-override
// doc (or null) — shared by both the confident and !confident branches below.
const parseOverride = (override) => ({
  overrideMode: override?.mode === "manual" ? "manual" : "calc",
  underLogPct: override?.underLogPct ?? 0,
  manualValue: override?.manualValue ?? null,
  hasOverride: Boolean(override),
});

// calc mode has nothing to apply the % to without an estExpenditure, so this
// is only meaningful in the confident branch — the !confident branch handles
// its own (manual-only) case inline, where there's no estExpenditure at all.
const applyOverride = (estExpenditure, { overrideMode, underLogPct, manualValue, hasOverride }) => {
  if (!hasOverride) return estExpenditure;
  if (overrideMode === "manual" && manualValue != null) return Math.round(manualValue);
  return Math.round(estExpenditure * (1 + underLogPct / 100));
};

// See RECENT_WINDOW_DAYS' own comment for the "why" — a window can clear the
// minLogged/minWeighIns gate entirely from data near its start.
const detectStaleData = (allDays, total) => {
  const recentDays = allDays.slice(-Math.min(RECENT_WINDOW_DAYS, total));
  const recentLogged = recentDays.filter((d) => d.logged && d.consumed).length;
  const recentWeighIns = recentDays.filter((d) => d.weight != null).length;
  return recentLogged === 0 && recentWeighIns === 0;
};

// Linear regression slope (y = weight, x = day index in the period).
// Exported for reuse in goal-projection (SummaryView useMemo).
export const weightSlopePerDay = (allDays, withWeight) => {
  const dayIndexMap = new Map(allDays.map((d, i) => [d.utcDate, i]));
  const n = withWeight.length;
  let sx = 0, sy = 0, sxy = 0, sxx = 0;
  for (const d of withWeight) {
    const x = dayIndexMap.get(d.utcDate) ?? 0;
    sx  += x;
    sy  += d.weight;
    sxy += x * d.weight;
    sxx += x * x;
  }
  const denom = n * sxx - sx * sx;
  return denom === 0 ? 0 : (n * sxy - sx * sy) / denom;
};

// Energy expenditure via weight-change back-calculation.
// Uses linear regression on weigh-ins (not endpoints) for a more robust slope.
// Returns { confident: false } when there is insufficient data.
//
// `override` is the raw nutrition-tdee-override doc (or null) — the coach's
// working-TDEE adjustment. Two modes: "calc" applies a %
// under-logging adjustment on top of the calculated estExpenditure;
// "manual" replaces it with an absolute kcal/day value. `calc` only makes
// sense when there's an estExpenditure to adjust (i.e. confident); `manual`
// does NOT need confident data — a coach can hand-enter a starting TDEE for
// a brand-new client with no logs yet, which is the one case where
// workingExpenditure can be non-null even though estExpenditure never got
// computed at all.
export const buildEnergyExpenditureStats = (dayRows, override = null) => {
  const { overrideMode, underLogPct, manualValue, hasOverride } = parseOverride(override);

  const allDays = dayRows.slice().reverse(); // oldest-first
  const total = allDays.length;
  const loggedWithIntake = allDays.filter((d) => d.logged && d.consumed);
  const withWeight = allDays.filter((d) => d.weight != null);

  if (
    total < EXPENDITURE_NEEDS.minDays ||
    loggedWithIntake.length < EXPENDITURE_NEEDS.minLogged ||
    withWeight.length < EXPENDITURE_NEEDS.minWeighIns
  ) {
    return {
      confident: false,
      totalDays: total,
      loggedDays: loggedWithIntake.length,
      weighInCount: withWeight.length,
      needs: EXPENDITURE_NEEDS,
      hasOverride,
      overrideMode,
      underLogPct,
      manualValue,
      workingExpenditure:
        overrideMode === "manual" && manualValue != null ? Math.round(manualValue) : null,
    };
  }

  const slope = weightSlopePerDay(allDays, withWeight); // kg/day
  const weeklyChange = slope * 7;                        // kg/week (signed)
  // slope is already a per-day rate, so this is just the unit conversion
  // (kg/day -> kcal/day via 7700 kcal/kg) — no division by day count needed.
  // A prior version derived this as slope*(total-1) kg over the period, then
  // divided by `total` (not `total-1`) to get back to a daily rate — that
  // extra round-trip introduced a systematic (total-1)/total bias, worst at
  // the minimum confident window (14 days: ~7% understated) and never fully
  // vanishing even in long windows (28 days: ~3.6%).
  const balancePerDay = Math.round(slope * 7700);

  const avgIntake = Math.round(
    loggedWithIntake.reduce((s, d) => s + d.consumed.kcal, 0) / loggedWithIntake.length
  );
  const estExpenditure = Math.round(avgIntake - balancePerDay);

  // Weight moving faster than fat alone can explain → likely water/glycogen shift.
  // The TDEE back-calc ingests that swing and produces a distorted number.
  const decoupled = Math.abs(weeklyChange) > DECOUPLING_THRESHOLD_KG_PER_WEEK;
  const staleData = detectStaleData(allDays, total);
  const workingExpenditure = applyOverride(estExpenditure, { overrideMode, underLogPct, manualValue, hasOverride });

  return {
    confident: true,
    decoupled,
    staleData,
    weeklyChange: Math.round(weeklyChange * 100) / 100,
    estExpenditure,
    workingExpenditure,
    // Also returned by the !confident branch above — kept on both so the
    // shape is consistent, and so TdeeExplainerModal can say "over N days"
    // without re-deriving it from dayRows.
    totalDays: total,
    hasOverride,
    overrideMode,
    underLogPct,
    manualValue,
    coachDelta: workingExpenditure - estExpenditure,
    deficit: Math.round(workingExpenditure - avgIntake), // was estExpenditure; now follows the working value
    avgIntake,
    weighInCount: withWeight.length,
    loggedDays: loggedWithIntake.length,
  };
};

// Suggested intake for a given rate (kg/wk). Negative = deficit, positive = surplus.
export const suggestedIntakeFor = (workingExpenditure, rateKgPerWeek) =>
  Math.round(workingExpenditure + (rateKgPerWeek * 7700) / 7);

/* ---- summary stats ----------------------------------------------------- */

// Aggregate stats for the Summary view metric cards.
//
// `todayUtc` (YYYYMMDD) caps Logging Rate's denominator at days that have
// actually happened — a coach can select a still-open range (e.g. "this
// plan", whose dateTo hasn't arrived yet), and buildDayRows fills every day
// through dateTo regardless, future ones included, always with logged:false
// since nothing occurred there yet. Counting those against the athlete
// silently deflates the rate (30 logged of 42 calendar days = 71%, when only
// 32 have actually elapsed and 30/32 = 94% is the real number) and can flip
// a near-perfect athlete's card into its amber "needs attention" state.
// Every other stat below already comes from `logged`/`withTarget`/etc. —
// subsets that future rows can never enter (they're never logged) — so
// `total` is the only count that needs the cap.
export const buildSummaryStats = (dayRows, todayUtc) => {
  // Every count over the range (not average — those already derive from
  // `logged`/`withTarget`, which future rows can never enter) has to stop at
  // today, or a still-open range charges the athlete for days that haven't
  // happened. `elapsed` is that shared cap: `total` and `workoutDays` both
  // read from it. dayRows is newest-first, so [0] is the range's last day.
  const elapsed = dayRows.filter((d) => d.utcDate <= todayUtc);
  const total = elapsed.length;
  const rangeEndUtc = dayRows.length ? dayRows[0].utcDate : null;
  const logged = dayRows.filter((d) => d.logged);
  const withConsumed = logged.filter((d) => d.consumed);
  const withTarget = logged.filter((d) => d.consumed && d.target);
  const training = withTarget.filter((d) => d.dayType === "training");
  const rest = withTarget.filter((d) => d.dayType === "rest");

  const mean = (arr, fn) =>
    arr.length ? arr.reduce((s, d) => s + fn(d), 0) / arr.length : null;
  const roundOrNull = (v) => (v == null ? null : Math.round(v));

  // A "hit" is defined by the SAME rule that paints the Table's protein cell
  // green — macroCol's "good" band (symmetric, |Δ|/target < macroGood).
  // It used to be its own one-sided floor (consumed >= 0.9 × target), which
  // meant the two views could contradict each other on identical data: a
  // client landing 92-99% of target every day scored "protein hit on 100% of
  // logged days" in Insights while the Table painted most of those same days
  // amber (outside its tighter green band), and overshooting by any amount
  // counted as a hit here but never in the Table. Reusing macroCol makes the
  // two mathematically unable to disagree — and keeps them agreeing if
  // ADHERENCE_THRESHOLDS is ever retuned, instead of drifting apart again.
  const withProtein = withTarget.filter((d) => d.target.proteins > 0);
  const proteinHitDays = withProtein.filter(
    (d) => macroCol(d.consumed.proteins, d.target.proteins) === "good"
  ).length;

  // dayRows is newest-first; last element = oldest weigh-in.
  const withWeight = dayRows.filter((d) => d.weight != null);
  const weightLast  = withWeight.length ? withWeight[0].weight                     : null;
  const weightFirst = withWeight.length ? withWeight[withWeight.length - 1].weight : null;

  const withSteps = dayRows.filter((d) => d.steps != null);
  const withSleep = dayRows.filter((d) => d.sleep != null);

  return {
    totalDays:  total,
    loggedDays: logged.length,
    loggingRate: total ? Math.round((logged.length / total) * 100) : 0,

    overallAdh:  roundOrNull(mean(withTarget, (d) => dayAdherence(d))),
    trainingAdh: roundOrNull(mean(training,   (d) => dayAdherence(d))),
    restAdh:     roundOrNull(mean(rest,       (d) => dayAdherence(d))),
    hasRestData: rest.length > 0,

    proteinHitDays,
    proteinHitOf:  withProtein.length,
    proteinHitRate: withProtein.length
      ? Math.round((proteinHitDays / withProtein.length) * 100)
      : null,

    avgIntake: roundOrNull(mean(withConsumed, (d) => d.consumed.kcal)),

    weightFirst,
    weightLast,
    weightDelta:
      weightFirst != null && weightLast != null
        ? Math.round((weightLast - weightFirst) * 10) / 10
        : null,

    avgSteps: roundOrNull(mean(withSteps, (d) => d.steps)),
    avgSleep: roundOrNull(mean(withSleep, (d) => d.sleep)), // ms
    workoutDays: elapsed.filter((d) => d.workout).length,

    // Drives the "so far" wording + ⓘ on every card showing one of the capped
    // counts above — without it those cards silently disagree with a coach
    // counting the calendar (which includes the not-yet-elapsed days).
    rangeEndUtc,
    rangeEndsInFuture: rangeEndUtc != null && rangeEndUtc > todayUtc,
  };
};

/* ---- chart series ------------------------------------------------------ */

// Hero chart series: one entry per day, oldest-first.
//   intake  — 7-day rolling avg kcal (logged days only; null if none in window)
//   target  — 7-day rolling avg target kcal (all days with a target)
//   weight  — raw weigh-in (null when no weigh-in that day)
//   name    — formatted date label ("May 4")
//   idx     — integer X-axis position (0 = oldest)
export const buildChartSeries = (dayRows) => {
  const days = dayRows.slice().reverse(); // oldest-first
  return days.map((day, i) => {
    const window = days.slice(Math.max(0, i - 6), i + 1);
    const loggedInWindow = window.filter((d) => d.logged && d.consumed);
    const withTarget = window.filter((d) => d.target);

    const intake = loggedInWindow.length
      ? Math.round(
          loggedInWindow.reduce((s, d) => s + d.consumed.kcal, 0) / loggedInWindow.length
        )
      : null;

    const target = withTarget.length
      ? Math.round(
          withTarget.reduce((s, d) => s + d.target.kcal, 0) / withTarget.length
        )
      : null;

    return {
      idx: i,
      utcDate: day.utcDate,
      name: moment(String(day.utcDate), "YYYYMMDD").format("MMM D"),
      intake,
      target,
      weight: day.weight,
    };
  });
};

/* ---- macro breakdown series ---------------------------------------------- */

// Splits a set of raw (unrounded) values into integer parts that sum EXACTLY
// to `total` — largest-remainder method. Rounding each part independently
// (plain Math.round) can disagree with Math.round(sum-of-parts) by ±1 or
// more once you have 2+ parts with fractional kcal (e.g. 10.15g protein +
// 10.15g carbs + 0g fat: independently-rounded parts sum to 82, but the
// already-rounded total is 81) — this guarantees the stacked bar's segments
// always add up to the "Consumed/Target kCal" figure shown in the tooltip.
// Mathematically remaining can never go negative here (sum of floors is
// always <= the true sum, which is always <= total + 0.5), the Math.max(0, )
// is just a defensive floor against floating-point edge cases.
const allocateIntParts = (rawByKey, total) => {
  const floors = Object.entries(rawByKey).map(([key, raw]) => [key, Math.floor(raw), raw - Math.floor(raw)]);
  let remaining = Math.max(0, total - floors.reduce((sum, [, floor]) => sum + floor, 0));
  const byRemainderDesc = [...floors].sort((a, b) => b[2] - a[2]);
  const result = Object.fromEntries(floors.map(([key, floor]) => [key, floor]));
  for (const [key] of byRemainderDesc) {
    if (remaining <= 0) break;
    result[key] += 1;
    remaining -= 1;
  }
  return result;
};

// Per-macro kcal contribution (protein/carbs 4 kcal/g, fat 9 kcal/g) — same
// factors as kcalFromMacros (adherence.js), just not collapsed to one total:
// the stacked bar needs each macro's own slice, allocated via
// allocateIntParts so the 3 segments sum to `totalKcal` (consumedKcal —
// itself already Math.round'ed elsewhere, in kcalFromMacros) exactly, not
// just approximately (kcal is the axis the Target line is drawn on too).
const macroKcals = ({ proteins = 0, carbs = 0, fats = 0 } = {}, totalKcal) => {
  const parts = allocateIntParts(
    { proteinsKcal: proteins * 4, carbsKcal: carbs * 4, fatsKcal: fats * 9 },
    totalKcal
  );
  return parts;
};

// Days the macro-breakdown chart AND its table are built from — shared so the
// two can never disagree on which days count.
const hasConsumedAndTarget = (d) => d.logged && d.consumed && d.target;

// One chart point from anything shaped { consumed, target } — a day row or an
// avgOf result — so daily, weekly and overall-average bars all carry the same
// fields the tooltip reads.
const macroPoint = ({ consumed, target }, extra) => ({
  ...extra,
  proteins: consumed.proteins,
  carbs: consumed.carbs,
  fats: consumed.fats,
  ...macroKcals(consumed, consumed.kcal),
  consumedKcal: consumed.kcal,
  targetKcal: target.kcal,
});

// Reports "Daily macro breakdown" chart: one bar per LOGGED day in the
// selected range, oldest-first, plus a trailing average entry — per the
// user's choice to show every logged day rather than capping to a fixed
// window (long ranges scroll horizontally instead, see ReportsMacroChart.js).
// The average entry reuses avgOf (adherence.js) — the SAME average Table/
// Calendar already show, so this bar never disagrees with those numbers.
// `avgLabel` is passed in (not translated here) — this stays a pure function
// with no i18n dependency, same as the rest of this module.
// { weekly: true } ("Show Averages only", as in the Classic view) swaps the
// per-day bars for one bar per ISO week — each the avgOf of that week's
// logged days — keeping the same trailing overall average. A week's bar is
// labelled with its Monday, clamped to the period's first day so a partial
// first week doesn't show a date from before the range.
export const buildMacroBreakdownSeries = (
  dayRows,
  avgLabel,
  { weekly = false } = {}
) => {
  const loggedWithTarget = dayRows
    .slice()
    .reverse() // oldest-first
    .filter(hasConsumedAndTarget);

  let bars;
  if (weekly) {
    const periodStart = dayRows.reduce(
      (min, d) => Math.min(min, d.utcDate),
      Infinity
    );
    bars = weekGroups(loggedWithTarget)
      .reverse() // weekGroups is newest-week-first
      .map((week) => {
        const monday = Number(
          moment(String(week[0].utcDate), "YYYYMMDD")
            .startOf("isoWeek")
            .format("YYYYMMDD")
        );
        const utcDate = Math.max(monday, periodStart);
        return macroPoint(avgOf(week), {
          utcDate,
          name: moment(String(utcDate), "YYYYMMDD").format("MMM D"),
        });
      });
  } else {
    bars = loggedWithTarget.map((day) =>
      macroPoint(day, {
        utcDate: day.utcDate,
        name: moment(String(day.utcDate), "YYYYMMDD").format("MMM D"),
      })
    );
  }

  const avg = avgOf(loggedWithTarget);
  if (avg.n > 0) {
    bars.push(macroPoint(avg, { utcDate: null, name: avgLabel }));
  }

  return bars;
};

/* ---- macro adherence table ------------------------------------------------ */

// kcal per gram — the "% of kcal" share columns are computed from macros
// (P×4 + C×4 + F×9) rather than the stored kcal, so the 3 shares always add
// up to 100 (same convention as kcalFromMacros).
const KCAL_PER_GRAM = { proteins: 4, carbs: 4, fats: 9 };

const sharesOf = (values) => {
  const totalKcal = Object.entries(KCAL_PER_GRAM).reduce(
    (sum, [m, k]) => sum + values[m] * k,
    0
  );
  return Object.fromEntries(
    Object.entries(KCAL_PER_GRAM).map(([m, k]) => [
      m,
      totalKcal ? Math.round(((values[m] * k) / totalKcal) * 100) : null,
    ])
  );
};

// Reports "Daily macro breakdown" table: per macro (protein/carbs/fat/fiber/
// kcal), Target vs Consumed vs Adherence over the same logged-with-target days
// the chart plots. mode "avg" = per-day average (matches the chart's Avg bar),
// "total" = sum over the period. adherence = target − consumed, so > 0 reads
// "Under" and < 0 "Over"; adherencePct is relative to target. shares (% of
// kcal from each macro) only apply to protein/carbs/fat. null when no day
// qualifies.
export const buildMacroAdherenceTable = (dayRows, mode = "avg") => {
  const days = dayRows.filter(hasConsumedAndTarget);
  if (!days.length) return null;

  const scale = mode === "total" ? 1 : 1 / days.length;
  const aggregate = (pick) =>
    Object.fromEntries(
      MACROS.map((m) => [
        m,
        Math.round(days.reduce((sum, d) => sum + (pick(d)[m] || 0), 0) * scale),
      ])
    );
  const target = aggregate((d) => d.target);
  const consumed = aggregate((d) => d.consumed);
  const targetShares = sharesOf(target);
  const consumedShares = sharesOf(consumed);

  return Object.fromEntries(
    MACROS.map((m) => {
      const adherence = target[m] - consumed[m];
      return [
        m,
        {
          target: target[m],
          consumed: consumed[m],
          adherence,
          adherencePct: target[m]
            ? Math.round((adherence / target[m]) * 100)
            : null,
          targetShare: targetShares[m] ?? null,
          consumedShare: consumedShares[m] ?? null,
        },
      ];
    })
  );
};

/* ---- weekday adherence series -------------------------------------------- */

// Reports "Adherence by day of week" chart: one entry per ISO weekday
// (Mon..Sun), each the average deviation-from-target across every logged day
// that fell on that weekday within the selected range — surfaces patterns a
// straight daily view can't (e.g. "adherence slips on weekends"). Reuses
// weekdayGroups (adherence.js) — its own comment already anticipated this
// exact chart ("Used by the Calendar bottom row and reused by the Graphs
// weekday chart"). dayDeviation, NOT dayAdherence (which is the inverted
// 100−deviation score) — the mockup's axis is "% off target" (0 = perfect,
// higher = worse), i.e. dayDeviation's own scale directly.
// `n` (days contributed) rides along so the chart's reference-line average
// can be day-weighted rather than an unweighted mean of 7 weekday means.
//
// avgKcalOff/kcalDirection back the tooltip's "Avg kcal off" row. The
// magnitude is the mean of the ABSOLUTE per-day misses, not of the signed
// ones: averaging signed deltas lets a +300 day and a −300 day cancel to 0
// ("perfect") when in fact both missed badly. Absolute keeps "how far off,
// typically" honest — and matches how pctOffTarget above already works
// (dayDeviation is itself absolute-valued).
// Direction is then reported separately, and only when it's actually
// consistent: with the magnitude decoupled from the sign, calling a
// half-over/half-under weekday "(over)" would be a coin flip dressed as a
// finding. Below the threshold it reports "mixed" instead.
const KCAL_DIRECTION_CONSISTENCY = 0.7;

const kcalOffStats = (rows) => {
  if (!rows.length) return { avgKcalOff: null, kcalDirection: null };
  const deltas = rows.map((d) => d.consumed.kcal - d.target.kcal);
  const avgKcalOff = Math.round(
    deltas.reduce((s, v) => s + Math.abs(v), 0) / deltas.length
  );
  const overShare = deltas.filter((v) => v > 0).length / deltas.length;
  const underShare = deltas.filter((v) => v < 0).length / deltas.length;
  let kcalDirection = "mixed";
  if (overShare >= KCAL_DIRECTION_CONSISTENCY) kcalDirection = "over";
  else if (underShare >= KCAL_DIRECTION_CONSISTENCY) kcalDirection = "under";
  return { avgKcalOff, kcalDirection };
};

export const buildWeekdayAdherenceSeries = (dayRows) => {
  const loggedWithTarget = dayRows.filter((d) => d.logged && d.consumed && d.target);
  const groups = weekdayGroups(loggedWithTarget); // index 0 = Mon … 6 = Sun

  return groups.map((rows, i) => {
    const rawMean = rows.length
      ? rows.reduce((s, d) => s + dayDeviation(d), 0) / rows.length
      : null;
    return {
      ...kcalOffStats(rows),
      weekday: moment().isoWeekday(i + 1).format("ddd"),
      pctOffTarget: rawMean == null ? null : Math.round(rawMean),
      // Unrounded per-weekday mean — ReportsWeekdayChart.js's reference line
      // averages THIS (weighted by n) instead of the rounded pctOffTarget
      // above, then rounds once. Averaging already-rounded per-weekday means
      // can disagree with the true overall mean by ±1 (same double-rounding
      // issue as the macro-breakdown chart's kcal split) — this field lets
      // the reference line stay mathematically exact while pctOffTarget
      // stays rounded for the bars/tooltip, which is fine to display rounded.
      pctOffTargetRaw: rawMean,
      n: rows.length,
    };
  });
};

/* ---- over/under-by-weekday series (signed deviation) ------------------- */

// Metric keys this chart can toggle between — same field names dayRow's
// consumed/target objects already use (MACROS minus fibers, dayData.js).
export const OVER_UNDER_METRICS = ["kcal", "proteins", "carbs", "fats"];

// Reports "Average over / under by day of week" chart: one entry per ISO
// weekday (Mon..Sun), a SIGNED average deviation for a single metric at a
// time — unlike dayDeviation/buildWeekdayAdherenceSeries, this never blends
// metrics together, so it's unaffected by that formula's own known weakness
// (kcal double-counting protein/carbs misses, tracked separately). Only days
// that are logged, carry both consumed and target data for `metric`, and
// have already elapsed (utcDate <= todayUtc) are included — same "no future
// days" rule as buildSummaryStats (see its own header comment for the why).
//
// signedAvg drives the bar's height/direction — NOT absAvg. Averaging signed
// per-day deltas lets over- and under-shoot days on the same weekday cancel
// toward the true net direction; averaging absolute deltas instead would
// report only magnitude, which can misrepresent a weekday as far worse than
// its real net drift (a Thursday of +300/-300/-350/-100 nets to a mild -42.8
// signed, not the much larger 182.8 absolute average — the two numbers
// answer different questions, and the bar's height must answer "which way").
export const buildOverUnderSeries = (dayRows, metric, todayUtc) => {
  const eligible = dayRows.filter(
    (d) => d.logged && d.consumed && d.target && d.target[metric] > 0 && d.utcDate <= todayUtc
  );
  const groups = weekdayGroups(eligible); // index 0 = Mon … 6 = Sun

  return groups.map((rows, i) => {
    const weekday = moment().isoWeekday(i + 1).format("ddd");
    // Locale-INDEPENDENT companion to `weekday` above (which is moment's own
    // locale-formatted abbreviation, e.g. "vie" in Spanish) — the tooltip's
    // pluralized weekday name ("4 logged Fridays") is a real i18n string
    // keyed by position (mon..sun), not derived from the locale-formatted
    // abbreviation, so this index is what the view layer looks it up by.
    const weekdayIndex = i + 1;
    if (!rows.length) {
      return { weekday, weekdayIndex, signedAvg: null, absAvg: null, pctOff: null, n: 0 };
    }

    const deltas = rows.map((d) => d.consumed[metric] - d.target[metric]);
    const pctDeltas = rows.map((d, idx) => (deltas[idx] / d.target[metric]) * 100);
    const round1 = (v) => Math.round(v * 10) / 10;

    return {
      weekday,
      weekdayIndex,
      signedAvg: round1(deltas.reduce((s, v) => s + v, 0) / deltas.length),
      absAvg: round1(deltas.reduce((s, v) => s + Math.abs(v), 0) / deltas.length),
      pctOff: round1(pctDeltas.reduce((s, v) => s + v, 0) / pctDeltas.length),
      n: rows.length,
    };
  });
};

// Fixed "nice" Y-axis maxima to pick from, so the chart's scale is always a
// clean round number (never an arbitrary auto-fit max) and stays legible
// when toggling between metrics with very different natural magnitudes
// (kcal in the hundreds vs. grams in the tens). 1.1x headroom keeps the
// tallest bar off the very edge of the plot.
const OVER_UNDER_AXIS_STEPS = [5, 10, 20, 25, 50, 80, 100, 150, 200, 300, 400, 500];

export const overUnderAxisMax = (maxAbsSignedAvg) => {
  const needed = maxAbsSignedAvg * 1.1;
  return OVER_UNDER_AXIS_STEPS.find((v) => v >= needed) ?? OVER_UNDER_AXIS_STEPS.at(-1);
};

/* ---- bodyweight goal resolution + projection --------------------------- */

// The one bodyweight goal currently in force: not completed, already started,
// and not past its own target date (an expired-but-not-marked-completed goal
// reads the same as "no goal" rather than resurrecting a stale one).
// Extracted from Nutrition.js so any consumer of `clientGoals` resolves the
// active goal the same way.
export const resolveActiveBodyweightGoal = (clientGoals, todayUtc) =>
  (clientGoals || []).find(
    (g) =>
      g.type === "bodyWeight" &&
      g.utcStartDate <= todayUtc &&
      (g.utcTargetDate == null || g.utcTargetDate >= todayUtc) &&
      !g.completed
  ) ?? null;

/* ---- daily (undated) goal resolution ------------------------------------ */

// Steps/sleep/waterIntake goals are undated by product convention (see
// PhaseSummary's "Info only — not dated" badge) - the schema allows a
// targetDate on any type, but these three are never given one in practice, so
// only utcStartDate gates whether a goal is "in force" (mirrors DailyGoals.js's
// existing resolution, which never checks utcTargetDate for these types either).
const resolveGoalOfType = (clientGoals, type, todayUtc) =>
  (clientGoals || []).find(
    (g) => g.type === type && g.utcStartDate <= todayUtc
  ) ?? null;

export const resolveDailyTargetGoals = (clientGoals, todayUtc) => ({
  steps: resolveGoalOfType(clientGoals, "steps", todayUtc),
  sleep: resolveGoalOfType(clientGoals, "sleep", todayUtc),
  waterIntake: resolveGoalOfType(clientGoals, "waterIntake", todayUtc),
});

// Custom goals (name + free-text target + coach-chosen icon) never carry a
// date at all in practice - same "info only" convention as the daily targets
// above, just with no single-type filter needed.
export const resolveCustomGoals = (clientGoals) =>
  (clientGoals || []).filter((g) => g.type === "custom" && !g.completed);

// Trend + projection for a bodyweight goal, from the client's weigh-in
// history: the RAW dailyStats list (`utcDate` + `bodyWeight`), not the dayRows
// scoped to the nutrition period the coach happens to have selected — the trend
// and the rate need weigh-ins from before that period. The numbers come from
// buildBodyweightTrend (bodyweightTrend.js), the same ones the Bodyweight trend
// card shows, reshaped into the fields the goal card, the Insights panel and
// Periodization's chip already read — so all of them agree: the 7-day trend as
// the current weight, the 4-week rate, the projection from the last weigh-in,
// and `paceState` (onPace / ahead / behind / offPace) as the one verdict on
// whether the client is on track.
// `todayUtc` is a caller-supplied param, not computed here with
// `toUtcNumber(new Date())` - SummaryView.js computes one shared `todayUtc`
// and passes it to both this and buildSummaryStats specifically so the two
// can never land on opposite sides of a midnight rollover mid-render (see
// that component's own comment: "was previously computed twice").
const toTenths = (value) => Math.round(value * 10) / 10;
const toHundredths = (value) => Math.round(value * 100) / 100;

export const buildGoalData = (bodyweightGoal, dailyStats, todayUtc) => {
  if (!bodyweightGoal) return null;
  const targetWeight = bodyweightGoal.details.targetWeight;
  const utcTargetDate = bodyweightGoal.utcTargetDate;
  const { _id } = bodyweightGoal;

  // No chart range is needed here, so the series is kept to a single day.
  const trend = buildBodyweightTrend({
    dailyStats,
    fromUtc: todayUtc,
    todayUtc,
    bodyweightGoal,
  });
  const currentWeight = trend.hasData ? trend.currentTrend : null;
  const ratePerWeek =
    trend.rateKgPerWeek === null ? null : toHundredths(trend.rateKgPerWeek);
  const { goal } = trend;

  // No weigh-ins, under 4 weeks of history for a rate, or a goal date that is
  // not after the last weigh-in: the goal still shows, without a projection.
  if (!goal || currentWeight === null || ratePerWeek === null) {
    return {
      _id,
      targetWeight,
      utcTargetDate,
      currentWeight,
      ratePerWeek,
      weeksToTarget: 0,
      todayUtc,
      movingToward: false,
      weeksToReach: null,
      reqRate: null,
      extraDeficit: null,
      projAtTarget: null,
      kgShort: null,
      paceState: null,
      isStale: trend.isProjectionStale,
      slopeSource: "trend",
    };
  }

  const needChange = toTenths(goal.toGo);
  const movingToward =
    Math.sign(ratePerWeek) === Math.sign(needChange) &&
    Math.abs(ratePerWeek) > 0.01;
  const weeksToReach = movingToward
    ? toTenths(Math.abs(needChange) / Math.abs(ratePerWeek))
    : null;
  const reqRate = toHundredths(goal.requiredRateKgPerWeek);
  const extraDeficit = Math.max(
    0,
    Math.round(((Math.abs(reqRate) - Math.abs(ratePerWeek)) * 7700) / 7)
  );
  // Projected weight at the target date (positive ratePerWeek = gaining, negative = losing).
  const projAtTarget = toTenths(goal.projectedWeight);
  // How far the projection falls short of the goal (positive = short, negative = ahead).
  const kgShort = toTenths(projAtTarget - targetWeight);

  return {
    _id,
    targetWeight,
    utcTargetDate,
    todayUtc,
    currentWeight,
    ratePerWeek,
    // Weeks from the last weigh-in (where currentWeight and the projection
    // start) to the goal date, so it compares like with like with weeksToReach.
    weeksToTarget: toTenths(goal.weeksToGoal),
    needChange,
    movingToward,
    weeksToReach,
    reqRate,
    extraDeficit,
    projAtTarget,
    kgShort,
    paceState: goal.paceState,
    isStale: false,
    slopeSource: "trend",
  };
};

/* ---- bodyweight goal projection series --------------------------------- */

// One point per day from the start of the selected range to the goal's
// target date — which usually extends BEYOND the range (the projection runs
// into the future). Two lines:
//   actual    — raw weigh-ins (oldest-first); null on days without one
//   projected — straight line from the last weigh-in (currentWeight) to the
//               target date (projAtTarget); null before the last weigh-in
// The projected endpoint equals goalData.projAtTarget so the chart never
// disagrees with the "Projected then" card. Returns [] when there's no rate.
export const buildGoalProjectionSeries = (dayRows, goalData) => {
  if (!dayRows.length || !goalData) return [];

  const oldest = dayRows.at(-1);             // dayRows is newest-first
  const startUtc = oldest.utcDate;
  const targetUtc = goalData.utcTargetDate;

  const weightByUtc = new Map(
    dayRows.filter((d) => d.weight != null).map((d) => [d.utcDate, d.weight])
  );

  // Last weigh-in anchors where the projection departs from the real line.
  const lastWeighIn = dayRows.find((d) => d.weight != null);
  const lastWeighInUtc = lastWeighIn ? lastWeighIn.utcDate : null;

  const { currentWeight, projAtTarget } = goalData;
  const hasProjection =
    projAtTarget != null && currentWeight != null && lastWeighInUtc != null;

  const startM = moment(String(startUtc), "YYYYMMDD");
  const endM = moment(String(targetUtc), "YYYYMMDD");
  const totalDays = Math.max(0, endM.diff(startM, "days"));
  const projSpanDays = hasProjection
    ? Math.max(1, endM.diff(moment(String(lastWeighInUtc), "YYYYMMDD"), "days"))
    : 0;

  const series = [];
  for (let i = 0; i <= totalDays; i++) {
    const dayM = startM.clone().add(i, "days");
    const utcDate = Number(dayM.format("YYYYMMDD"));

    let projected = null;
    if (hasProjection && utcDate >= lastWeighInUtc) {
      const daysIn = dayM.diff(moment(String(lastWeighInUtc), "YYYYMMDD"), "days");
      const t = daysIn / projSpanDays;
      projected = Math.round((currentWeight + (projAtTarget - currentWeight) * t) * 100) / 100;
    }

    series.push({
      idx: i,
      utcDate,
      name: dayM.format("MMM D"),
      actual: weightByUtc.has(utcDate) ? weightByUtc.get(utcDate) : null,
      projected,
    });
  }

  return series;
};
